import React, { useState, useEffect } from 'react'
import { Users, ArrowLeft, Plus, Edit, CheckCircle, Clock, AlertCircle } from 'lucide-react'
import { RestaurantTable, Order, Staff } from '../types'
import { supabase } from '../lib/supabase'

interface HandheldTableManagementProps {
  currentStaff: Staff
  onBack: () => void
}

const HandheldTableManagement: React.FC<HandheldTableManagementProps> = ({ currentStaff, onBack }) => {
  const [tables, setTables] = useState<RestaurantTable[]>([])
  const [orders, setOrders] = useState<Order[]>([])
  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null)

  useEffect(() => {
    fetchTables()
    fetchOrders()
  }, [])

  const fetchTables = async () => {
    try {
      const { data, error } = await supabase
        .from('restaurant_tables')
        .select('*')
        .order('table_number')

      if (error) {
        console.error('Error fetching tables:', error)
        // Mock data for demo
        setTables([
          { id: '1', table_number: 1, seats: 2, section: 'window', status: 'available', x_position: 100, y_position: 100, created_at: '' },
          { id: '2', table_number: 2, seats: 4, section: 'window', status: 'occupied', x_position: 200, y_position: 100, created_at: '' },
          { id: '3', table_number: 3, seats: 4, section: 'main', status: 'available', x_position: 100, y_position: 200, created_at: '' },
          { id: '4', table_number: 4, seats: 6, section: 'main', status: 'reserved', x_position: 200, y_position: 200, created_at: '' },
          { id: '5', table_number: 5, seats: 2, section: 'bar', status: 'available', x_position: 300, y_position: 100, created_at: '' },
          { id: '6', table_number: 6, seats: 8, section: 'private', status: 'cleaning', x_position: 400, y_position: 200, created_at: '' },
        ])
      } else {
        setTables(data || [])
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .neq('status', 'completed')
        .order('created_at', { ascending: true })

      if (error) {
        console.error('Error fetching orders:', error)
        setOrders([
          {
            id: '1',
            order_number: 1001,
            table_number: 2,
            customer_name: 'John Smith',
            order_type: 'dine_in',
            status: 'preparing',
            total_amount: 45.97,
            payment_status: 'paid',
            payment_method: 'card',
            items: [],
            created_at: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
            updated_at: new Date().toISOString()
          }
        ])
      } else {
        setOrders(data || [])
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const updateTableStatus = async (tableId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('restaurant_tables')
        .update({ status })
        .eq('id', tableId)

      if (error) {
        console.error('Error updating table:', error)
      }
      
      setTables(tables.map(table =>
        table.id === tableId ? { ...table, status: status as any } : table
      ))
      setSelectedTable(null)
    } catch (error) {
      console.error('Error:', error)
      setTables(tables.map(table =>
        table.id === tableId ? { ...table, status: status as any } : table
      ))
      setSelectedTable(null)
    }
  }

  const getTableColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 border-green-400 text-green-800'
      case 'occupied': return 'bg-red-100 border-red-400 text-red-800'
      case 'reserved': return 'bg-yellow-100 border-yellow-400 text-yellow-800'
      case 'cleaning': return 'bg-gray-100 border-gray-400 text-gray-800'
      default: return 'bg-gray-100 border-gray-400 text-gray-800'
    }
  }

  const getTableOrder = (tableNumber: number) => {
    return orders.find(order => order.table_number === tableNumber)
  }

  if (selectedTable) {
    const order = getTableOrder(selectedTable.table_number)
    
    return (
      <div className="max-w-md mx-auto bg-white min-h-screen">
        {/* Header */}
        <div className="bg-purple-600 text-white p-4">
          <div className="flex items-center">
            <button
              onClick={() => setSelectedTable(null)}
              className="mr-3 p-2 hover:bg-purple-700 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold">Table {selectedTable.table_number}</h1>
              <p className="text-purple-100 text-sm">{selectedTable.seats} seats</p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Current Status */}
          <div className={`p-4 rounded-lg border-2 ${getTableColor(selectedTable.status)}`}>
            <div className="text-center">
              <div className="text-lg font-bold capitalize mb-2">{selectedTable.status}</div>
              {order && (
                <div className="text-sm">
                  <div>Order #{order.order_number}</div>
                  <div className="capitalize">{order.status}</div>
                  {order.customer_name && <div>{order.customer_name}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Status Change Buttons */}
          <div className="space-y-3">
            <h3 className="font-bold text-gray-800">Change Status</h3>
            <div className="grid grid-cols-2 gap-3">
              {['available', 'occupied', 'reserved', 'cleaning'].map(status => (
                <button
                  key={status}
                  onClick={() => updateTableStatus(selectedTable.id, status)}
                  disabled={selectedTable.status === status}
                  className={`p-4 rounded-lg font-bold capitalize transition-colors ${
                    selectedTable.status === status
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {/* Order Details */}
          {order && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-3">Order Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Order #:</span>
                  <span className="font-bold">{order.order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className="font-bold capitalize">{order.status}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total:</span>
                  <span className="font-bold text-green-600">£{order.total_amount.toFixed(2)}</span>
                </div>
                {order.customer_name && (
                  <div className="flex justify-between">
                    <span>Customer:</span>
                    <span className="font-bold">{order.customer_name}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="bg-purple-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={onBack}
              className="mr-3 p-2 hover:bg-purple-700 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold">Table Management</h1>
              <p className="text-purple-100 text-sm">Manage table status</p>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 bg-gray-50">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
            <span>Available</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-400 rounded-full mr-2"></div>
            <span>Occupied</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-yellow-400 rounded-full mr-2"></div>
            <span>Reserved</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
            <span>Cleaning</span>
          </div>
        </div>
      </div>

      {/* Tables */}
      <div className="p-4 pb-20">
        <div className="grid grid-cols-3 gap-3">
          {tables.map(table => {
            const order = getTableOrder(table.table_number)
            // Force table to be occupied if it has an active order
            const actualStatus = order ? 'occupied' : table.status
            return (
              <button
                key={table.id}
                onClick={() => setSelectedTable(table)}
                className={`p-4 rounded-lg border-2 transition-all hover:scale-105 ${getTableColor(actualStatus)}`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {table.table_number}
                  </div>
                  <div className="flex items-center justify-center text-xs mb-2">
                    <Users size={12} className="mr-1" />
                    {table.seats}
                  </div>
                  <div className="text-xs font-bold capitalize mb-1">
                    {actualStatus}
                  </div>
                  
                  {order && (
                    <div className="text-xs bg-white bg-opacity-70 rounded p-1">
                      <div className="font-bold">#{order.order_number}</div>
                      <div className="capitalize">{order.status}</div>
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default HandheldTableManagement